import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import { Accordion } from '@decisiv/ui-components';
import AudienceFilter from './AudienceFilter';
import CategoryFilter from './CategoryFilter';
import CommandsFilter from './CommandsFilter';
import ServiceNetworkFilter from './ServiceNetworkFilter';
import TriggerFilter from './TriggerFilter';
import VisibilityFilter from './VisibilityFilter';
import CacheFilter from './CacheFilter';
import AutoRetryFilter from './AutoRetryFilter';
import CircuitBreakerFilter from './CircuitBreakerFilter';
import MetadataFilter from './MetadataFilter';
import ExecutionConditionsFilter from './ExecutionConditionsFilter';

// Panels read the live form/handleForm from context so each panel component can
// keep a stable identity across renders. Defining them inline gave every panel a
// new function identity on each keystroke, which made the KDS Accordion treat it
// as a new component type and remount the panel — destroying focus in any text
// input it holds (PD-4129).
const FilterContext = createContext({ form: {}, handleForm: () => {} });

function CategoryPanel() {
  const { form, handleForm } = useContext(FilterContext);
  return (
    <CategoryFilter handleForm={handleForm} currentValue={form.category} />
  );
}

function AudiencePanel() {
  const { form, handleForm } = useContext(FilterContext);
  return (
    <AudienceFilter handleForm={handleForm} currentValue={form.audience} />
  );
}

function ServiceNetworkPanel() {
  const { form, handleForm } = useContext(FilterContext);
  return (
    <ServiceNetworkFilter
      handleForm={handleForm}
      currentValue={form.serviceNetwork}
    />
  );
}

function TriggerPanel() {
  const { form, handleForm } = useContext(FilterContext);
  return (
    <TriggerFilter
      handleForm={handleForm}
      currentTriggerValue={form.trigger}
      currentEventsValue={form.events}
      currentActionsValue={form.actions}
    />
  );
}

function CommandsPanel() {
  const { form, handleForm } = useContext(FilterContext);
  return (
    <CommandsFilter handleForm={handleForm} currentValue={form.commands} />
  );
}

function VisibilityPanel() {
  const { form, handleForm } = useContext(FilterContext);
  return (
    <VisibilityFilter
      handleForm={handleForm}
      currentActiveValue={form.active}
      currentPublishValue={form.published}
    />
  );
}

function CachePanel() {
  const { form, handleForm } = useContext(FilterContext);
  return <CacheFilter handleForm={handleForm} currentValue={form.cache} />;
}

function CircuitBreakerPanel() {
  const { form, handleForm } = useContext(FilterContext);
  return (
    <CircuitBreakerFilter
      handleForm={handleForm}
      currentValue={form.circuitBreaker}
    />
  );
}

function AutoRetryPanel() {
  const { form, handleForm } = useContext(FilterContext);
  return (
    <AutoRetryFilter handleForm={handleForm} currentValue={form.autoRetry} />
  );
}

function MetadataPanel() {
  const { form, handleForm } = useContext(FilterContext);
  return (
    <MetadataFilter
      handleForm={handleForm}
      currentValue={form.metadata}
      currentMetadataKey={form.metadataKey}
      currentMetadataValue={form.metadataValue}
    />
  );
}

function ExecutionConditionsPanel() {
  const { form, handleForm } = useContext(FilterContext);
  return (
    <ExecutionConditionsFilter
      handleForm={handleForm}
      currentValue={form.executionConditions}
    />
  );
}

export default function AccordionFilter({ handleForm, form }) {
  const accordionItems = [
    {
      PanelComponent: CategoryPanel,
      id: 'accordion-category',
      title: t`Category`,
    },
    {
      PanelComponent: AudiencePanel,
      id: 'accordion-audience',
      title: t`Audience`,
    },
    {
      PanelComponent: ServiceNetworkPanel,
      id: 'accordion-service-network',
      title: t`Service Network`,
    },
    {
      PanelComponent: TriggerPanel,
      id: 'accordion-trigger',
      title: t`Trigger`,
    },
    {
      PanelComponent: CommandsPanel,
      id: 'accordion-commands',
      title: t`Commands`,
    },
    {
      PanelComponent: VisibilityPanel,
      id: 'accordion-visibility',
      title: t`Visibility`,
    },
    {
      PanelComponent: CachePanel,
      id: 'accordion-cache',
      title: t`Cache`,
    },
    {
      PanelComponent: CircuitBreakerPanel,
      id: 'accordion-circuit-breaker',
      title: t`Circuit Breaker`,
    },
    {
      PanelComponent: AutoRetryPanel,
      id: 'accordion-auto-retry',
      title: t`Automatic Retry`,
    },
    {
      PanelComponent: MetadataPanel,
      id: 'accordion-metadata',
      title: t`Metadata`,
    },
    {
      PanelComponent: ExecutionConditionsPanel,
      id: 'accordion-execution-conditions',
      title: t`Execution Conditions`,
    },
  ];

  // A fresh object here re-renders every consumer of the filter context on
  // each parent render, regardless of whether the form actually changed.
  const filterContext = useMemo(
    () => ({ form, handleForm }),
    [form, handleForm],
  );

  return (
    <FilterContext.Provider value={filterContext}>
      <Accordion items={accordionItems} titleHeadingLevel="5" controls="hide" />
    </FilterContext.Provider>
  );
}

AccordionFilter.propTypes = {
  handleForm: PropTypes.func.isRequired,
  form: PropTypes.oneOfType([PropTypes.object]).isRequired,
};
